import { t } from "@travily/i18n";
import type ProductsCard from "@travily/types/models/ProductsCard";
import type {
  ProductsCardPhotoTrait,
  ProductsCardPricingTrait,
  ProductsCardPublishTrait,
  ProductsCardSetupTrait,
} from "@travily/types/models/ProductsCard";
import type ServicesCard from "@travily/types/models/ServicesCard";
import type {
  ServicesCardPhotoTrait,
  ServicesCardPricingTrait,
  ServicesCardPublishTrait,
  ServicesCardSetupTrait,
} from "@travily/types/models/ServicesCard";
import { CardGrid } from "@ui/components/CardGrid";
import { EmptyState } from "@ui/components/EmptyState";
import { Heading } from "@ui/components/Heading";
import { Illustration } from "@ui/components/Illustration";
import { PayoutsBanner, type PayoutsStatus } from "./components/PayoutsBanner";
import { SellerListingCard } from "./components/SellerListingCard";

type SellerProduct = ProductsCard &
  ProductsCardPhotoTrait &
  ProductsCardPricingTrait &
  ProductsCardSetupTrait &
  ProductsCardPublishTrait;
type SellerService = ServicesCard &
  ServicesCardPhotoTrait &
  ServicesCardPricingTrait &
  ServicesCardSetupTrait &
  ServicesCardPublishTrait;

export interface IndexViewProps {
  products: SellerProduct[];
  services: SellerService[];
  payoutsStatus: PayoutsStatus | null;
  onSetUpPayouts: () => void;
  onResumePayouts: () => void;
}

export function IndexView({ products, services, payoutsStatus, onSetUpPayouts, onResumePayouts }: IndexViewProps) {
  const listings = [
    ...products.map((p) => ({ ...p, _type: "product" as const })),
    ...services.map((s) => ({ ...s, _type: "service" as const })),
  ];
  const hasListings = listings.length > 0;

  return (
    <div className="flex flex-col gap-6 py-8">
      {payoutsStatus && payoutsStatus !== "completed" ? (
        <PayoutsBanner status={payoutsStatus} onSetUpPayouts={onSetUpPayouts} onResumePayouts={onResumePayouts} />
      ) : null}
      <Heading.Root color="primary" size="lg">
        <Heading.Title as="h1">
          {t("components.views.creator.index_view.title", { count: listings.length })}
        </Heading.Title>
      </Heading.Root>
      {hasListings ? (
        <CardGrid>
          {listings.map((listing) => (
            <SellerListingCard key={listing.sqid} type={listing._type} listing={listing} />
          ))}
        </CardGrid>
      ) : (
        <EmptyState
          description={t("components.views.creator.index_view.empty_description")}
          illustration={<Illustration name="products" />}
          title={t("components.views.creator.index_view.empty_title")}
        />
      )}
    </div>
  );
}
